/**
 * OAuth 2.0 Authorization Code + PKCE flow for the standalone 7Punches terminal.
 *
 * Split into small pure functions plus a pair of higher-level orchestration
 * helpers (`initiateLogin`, `completeLogin`) that do the side-effectful work
 * (sessionStorage, fetch, window redirects).
 *
 * Tokens are never written to localStorage — callers store them in memory via
 * AuthContext. sessionStorage is only used for the PKCE verifier, the CSRF
 * state, and the post-login redirect target — all tab-scoped and cleared
 * after the exchange completes.
 */

import { env } from '../config/env';
import {
    deriveCodeChallenge,
    generateCodeVerifier,
    generateState
} from './pkce';

/**
 * Raw FusionAuth /oauth2/token response (RFC 6749 §5.1 snake_case shape).
 * Used by `refreshAccessToken` — callers receive the server payload directly.
 */
export interface RawTokenResponse {
    access_token: string;
    refresh_token?: string;
    /** Lifetime of the access token in seconds. */
    expires_in: number;
    token_type: string;
}

/** Normalized token response returned by `exchangeCodeForTokens`. */
export interface TokenResponse {
    accessToken: string;
    refreshToken: string | null;
    expiresIn: number;
    tokenType: string;
}

/** Storage keys used during the redirect round-trip. All tab-scoped (sessionStorage). */
const PKCE_VERIFIER_KEY = '7punches:pkce_verifier';
const OAUTH_STATE_KEY = '7punches:oauth_state';
/** Exported so route guards can stash a deep-link target before redirecting to /login. */
export const POST_LOGIN_REDIRECT_KEY = '7punches:post_login_redirect';
/**
 * Stashed by LoginPage before the FusionAuth redirect when running in harness
 * mode (?harness=1). Read by CallbackPage to pin the postMessage target origin.
 */
export const HARNESS_OPENER_ORIGIN_KEY = '7punches:harness_opener_origin';

/** Default OAuth scopes. `offline_access` is required to receive a refresh_token. */
const DEFAULT_SCOPES = ['offline_access'];

/** Compute the redirect_uri. The registered FusionAuth app must allow this exact URL. */
export function getRedirectUri(): string {
    return `${window.location.origin}/callback`;
}

/** Build the `/oauth2/authorize` URL with all required PKCE + OAuth params. */
export function buildAuthorizeUrl(params: {
    clientId: string;
    redirectUri: string;
    codeChallenge: string;
    state: string;
    scopes?: string[];
    authority: string;
}): string {
    const url = new URL('/oauth2/authorize', params.authority);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', params.clientId);
    url.searchParams.set('redirect_uri', params.redirectUri);
    url.searchParams.set('code_challenge', params.codeChallenge);
    url.searchParams.set('code_challenge_method', 'S256');
    url.searchParams.set('state', params.state);
    url.searchParams.set('scope', (params.scopes ?? DEFAULT_SCOPES).join(' '));
    return url.toString();
}

/**
 * Build the `/oauth2/logout` URL that clears FusionAuth's browser SSO cookie.
 * Mirrors `buildAuthorizeUrl` for symmetry. No `tenant_id` — `client_id`
 * alone is sufficient for FusionAuth to resolve the tenant.
 */
export function buildLogoutUrl(params: {
    clientId: string;
    postLogoutRedirectUri: string;
    authority: string;
}): string {
    const url = new URL('/oauth2/logout', params.authority);
    url.searchParams.set('client_id', params.clientId);
    url.searchParams.set(
        'post_logout_redirect_uri',
        params.postLogoutRedirectUri
    );
    return url.toString();
}

/** Raised when the OAuth token endpoint returns a non-2xx response or invalid payload. */
export class TokenExchangeError extends Error {
    constructor(message: string, public readonly cause?: unknown) {
        super(message);
        this.name = 'TokenExchangeError';
    }
}

/**
 * Raised when `refreshAccessToken`'s `fetch` itself throws (offline, DNS
 * failure, connection refused, etc.) — i.e. we never reached the token
 * endpoint at all. Distinct from a non-OK HTTP response (which means the
 * server was reachable and rejected the request) so callers can avoid
 * treating a connectivity blip as an auth rejection.
 */
export class RefreshNetworkError extends Error {
    constructor(message: string, public readonly cause?: unknown) {
        super(message);
        this.name = 'RefreshNetworkError';
    }
}

/**
 * POST the authorization code to `/oauth2/token` and parse the token response.
 *
 * `tokenEndpointBase` is intentionally separate from the authorize authority:
 * the authorize redirect must hit FusionAuth directly (browser redirect), but
 * the token exchange goes through the webapp proxy so we can piggy-back on
 * first-party cookies/CORS and any additional server-side enrichment.
 */
export async function exchangeCodeForTokens(params: {
    code: string;
    verifier: string;
    clientId: string;
    redirectUri: string;
    tokenEndpointBase: string;
}): Promise<TokenResponse> {
    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('code', params.code);
    body.set('code_verifier', params.verifier);
    body.set('client_id', params.clientId);
    body.set('redirect_uri', params.redirectUri);

    let response: Response;
    try {
        response = await fetch(
            new URL('/oauth2/token', params.tokenEndpointBase).toString(),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: body.toString()
            }
        );
    } catch (cause) {
        throw new TokenExchangeError(
            'Network error during token exchange',
            cause
        );
    }

    if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new TokenExchangeError(
            `Token exchange failed with status ${response.status}: ${detail}`
        );
    }

    const payload: unknown = await response.json().catch(() => null);
    if (
        !payload ||
        typeof payload !== 'object' ||
        typeof (payload as { access_token?: unknown }).access_token !==
            'string' ||
        typeof (payload as { expires_in?: unknown }).expires_in !== 'number'
    ) {
        throw new TokenExchangeError(
            'Token endpoint returned an invalid payload'
        );
    }

    const raw = payload as {
        access_token: string;
        refresh_token?: string;
        expires_in: number;
        token_type?: string;
    };
    return {
        accessToken: raw.access_token,
        refreshToken: raw.refresh_token ?? null,
        expiresIn: raw.expires_in,
        tokenType: raw.token_type ?? 'Bearer'
    };
}

/**
 * Exchange a refresh token for a new access token via the webapp proxy.
 * Returns the raw RFC 6749 snake_case payload.
 */
export async function refreshAccessToken(
    refreshToken: string
): Promise<RawTokenResponse> {
    let response: Response;
    try {
        response = await fetch(`${env.apiBaseUrl}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                client_id: env.oauthClientId,
                grant_type: 'refresh_token',
                refresh_token: refreshToken
            })
        });
    } catch (cause) {
        // The fetch itself threw — we never reached the token endpoint, so
        // this is a connectivity failure, not an auth rejection. Callers
        // must not treat this as grounds to expire the session.
        throw new RefreshNetworkError(
            'Network error during token refresh',
            cause
        );
    }

    if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(
            `Token refresh failed (${response.status}): ${
                body || response.statusText
            }`
        );
    }

    let rawBody: string;
    try {
        rawBody = await response.text();
    } catch (cause) {
        // Headers arrived (fetch resolved) but reading the body stream failed
        // — a connection drop mid-response. This is a connectivity failure,
        // not an auth rejection, so surface it as a RefreshNetworkError to keep
        // callers from expiring the session (API-4800).
        throw new RefreshNetworkError(
            'Network error reading token refresh response',
            cause
        );
    }

    try {
        return JSON.parse(rawBody) as RawTokenResponse;
    } catch (cause) {
        // The body was read in full but isn't valid JSON. The server was
        // reachable and returned a 2xx, so this is NOT a connectivity failure
        // — it must not be wrapped as a RefreshNetworkError (which would wrongly
        // flip the app offline and defer). Treat it as an ordinary refresh
        // failure instead.
        throw new Error(
            `Token refresh returned an unparseable response body: ${
                cause instanceof Error ? cause.message : String(cause)
            }`
        );
    }
}

/**
 * Begin the login flow: generate PKCE material, stash verifier+state for the
 * callback, remember where to return the user to, and redirect the browser to
 * FusionAuth's authorize endpoint.
 */
export async function initiateLogin(options?: {
    postLoginRedirect?: string;
}): Promise<void> {
    const verifier = generateCodeVerifier();
    const challenge = await deriveCodeChallenge(verifier);
    const state = generateState();

    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    if (options?.postLoginRedirect) {
        sessionStorage.setItem(
            POST_LOGIN_REDIRECT_KEY,
            options.postLoginRedirect
        );
    }

    const authorizeUrl = buildAuthorizeUrl({
        clientId: env.oauthClientId,
        redirectUri: getRedirectUri(),
        codeChallenge: challenge,
        state,
        authority: env.oauthAuthority
    });

    window.location.assign(authorizeUrl);
}

export interface CallbackResult {
    tokens: TokenResponse;
    /** Where the caller should navigate after storing the tokens. Defaults to "/". */
    redirectTo: string;
}

/**
 * Complete the flow on the `/callback` route: validate state, retrieve the
 * stashed verifier, exchange the authorization code for tokens, and clean up.
 */
export async function completeLogin(
    searchParams: URLSearchParams
): Promise<CallbackResult> {
    const error = searchParams.get('error');
    if (error) {
        clearSessionStorage();
        throw new TokenExchangeError(
            `Authorization server returned error: ${error}${
                searchParams.get('error_description')
                    ? ` (${searchParams.get('error_description')})`
                    : ''
            }`
        );
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code || !state) {
        clearSessionStorage();
        throw new TokenExchangeError(
            'Callback is missing required `code` or `state` parameter'
        );
    }

    const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!expectedState || state !== expectedState) {
        clearSessionStorage();
        throw new TokenExchangeError(
            'OAuth state mismatch — possible CSRF attempt'
        );
    }
    if (!verifier) {
        clearSessionStorage();
        throw new TokenExchangeError(
            'Missing PKCE verifier — cannot complete token exchange'
        );
    }

    const tokens = await exchangeCodeForTokens({
        code,
        verifier,
        clientId: env.oauthClientId,
        redirectUri: getRedirectUri(),
        tokenEndpointBase: env.apiBaseUrl
    });

    const redirectTo =
        sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) ?? '/terminal';
    clearSessionStorage();

    return { tokens, redirectTo };
}

function clearSessionStorage(): void {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(HARNESS_OPENER_ORIGIN_KEY);
}

/** Exported for tests. */
export const __storageKeys = {
    verifier: PKCE_VERIFIER_KEY,
    state: OAUTH_STATE_KEY,
    redirect: POST_LOGIN_REDIRECT_KEY,
    harnessOpenerOrigin: HARNESS_OPENER_ORIGIN_KEY
};
